import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { compressImage } from "@/lib/imageCompress";
import { useAuth } from "@/context/AuthContext";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Stethoscope,
  TestTube2,
  ClipboardList,
  Package,
  Plus,
  Trash2,
  Pencil,
  Upload,
  Clock,
  Lock,
  Copy,
  ExternalLink,
} from "lucide-react";

const TABS = [
  { key: "doctors", label: "Doctors", icon: Stethoscope },
  { key: "appointments", label: "Appointments", icon: ClipboardList },
  { key: "catalog", label: "Test catalog", icon: TestTube2 },
  { key: "orders", label: "Pathology orders", icon: Package },
];

const DAYS = [
  { key: "mon", label: "Mon" },
  { key: "tue", label: "Tue" },
  { key: "wed", label: "Wed" },
  { key: "thu", label: "Thu" },
  { key: "fri", label: "Fri" },
  { key: "sat", label: "Sat" },
  { key: "sun", label: "Sun" },
];

const IST_STAMP = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Asia/Kolkata",
      hour12: false,
    });
  } catch {
    return "—";
  }
};

const IST_SLOT = (iso) => {
  try {
    return new Date(iso).toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone: "Asia/Kolkata",
    });
  } catch {
    return iso;
  }
};

const todayStr = () => new Date().toISOString().slice(0, 10);

function NotEnabled() {
  return (
    <div className="p-6 md:p-10 max-w-2xl">
      <Card className="border-slate-200 shadow-none">
        <CardContent className="p-8 text-center space-y-3">
          <Lock className="w-8 h-8 text-slate-300 mx-auto" />
          <div className="text-lg font-medium">Healthcare module isn't enabled</div>
          <p className="text-sm text-slate-500">
            Doctor booking and pathology services aren't turned on for your account yet.
            Ask your super admin to enable it from the Admins page.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

export default function Healthcare() {
  const { user } = useAuth();
  const [tab, setTab] = useState("doctors");
  const [enabled, setEnabled] = useState(null); // null = checking, true/false once known
  const [slug, setSlug] = useState("");

  // ---- Doctors ----
  const [employees, setEmployees] = useState([]);
  const [profileFor, setProfileFor] = useState(null); // employee being edited
  const [profileForm, setProfileForm] = useState({ specialization: "", consult_fee: 0 });
  const [savingProfile, setSavingProfile] = useState(false);

  const [slotsFor, setSlotsFor] = useState(null); // employee whose slots are open
  const [slots, setSlots] = useState([]);
  const [slotForm, setSlotForm] = useState({ day_of_week: "mon", start_time: "09:00", end_time: "13:00", slot_minutes: 20 });
  const [savingSlot, setSavingSlot] = useState(false);

  // ---- Appointments ----
  const [appointments, setAppointments] = useState([]);
  const [newApptOpen, setNewApptOpen] = useState(false);
  const [newAppt, setNewAppt] = useState({
    doctor_id: "", date: todayStr(), slot_start: "", patient_name: "", patient_phone: "", consent: false, relation_note: "",
  });
  const [newApptSlots, setNewApptSlots] = useState([]);
  const [loadingNewApptSlots, setLoadingNewApptSlots] = useState(false);
  const [savingAppt, setSavingAppt] = useState(false);
  const [apptExistingName, setApptExistingName] = useState(null);
  const apptNameConflict =
    apptExistingName && newAppt.patient_name.trim() &&
    apptExistingName.trim().toLowerCase() !== newAppt.patient_name.trim().toLowerCase();
  const checkApptPhone = async () => {
    if (!newAppt.patient_phone.trim()) return;
    try {
      const { data } = await api.get("/care/patient-lookup", {
        params: { owner_id: user.id, phone: newAppt.patient_phone },
      });
      setApptExistingName(data?.exists ? data.name : null);
    } catch {
      setApptExistingName(null);
    }
  };
  const doctors = employees.filter((e) => e.specialization);

  // ---- Pathology catalog ----
  const [catalog, setCatalog] = useState([]);
  const [testDialog, setTestDialog] = useState(null); // {id?, name, price, sample_type, report_tat_hours}
  const [savingTest, setSavingTest] = useState(false);

  // ---- Pathology orders ----
  const [orders, setOrders] = useState([]);
  const [uploadingReport, setUploadingReport] = useState(null); // order id in flight
  const [newOrderOpen, setNewOrderOpen] = useState(false);
  const [newOrderTests, setNewOrderTests] = useState(() => new Set());
  const [newOrder, setNewOrder] = useState({
    collection_mode: "walk_in", address: "", collector_id: "",
    patient_name: "", patient_phone: "", consent: false, relation_note: "",
  });
  const [savingOrder, setSavingOrder] = useState(false);
  const [orderExistingName, setOrderExistingName] = useState(null);
  const orderNameConflict =
    orderExistingName && newOrder.patient_name.trim() &&
    orderExistingName.trim().toLowerCase() !== newOrder.patient_name.trim().toLowerCase();
  const checkOrderPhone = async () => {
    if (!newOrder.patient_phone.trim()) return;
    try {
      const { data } = await api.get("/care/patient-lookup", {
        params: { owner_id: user.id, phone: newOrder.patient_phone },
      });
      setOrderExistingName(data?.exists ? data.name : null);
    } catch {
      setOrderExistingName(null);
    }
  };

  const loadAll = useCallback(async () => {
    try {
      const [emp, appts, cat, ord] = await Promise.all([
        api.get("/employees"),
        api.get("/healthcare/appointments"),
        api.get("/healthcare/pathology-catalog"),
        api.get("/healthcare/pathology-orders"),
      ]);
      setEmployees(emp.data || []);
      setAppointments(appts.data || []);
      setCatalog(cat.data || []);
      setOrders(ord.data || []);
      setEnabled(true);
    } catch (e) {
      if (e?.response?.status === 403) setEnabled(false);
      else toast.error(e?.response?.data?.detail || "Could not load healthcare data");
    }
    // Best-effort — the share-link banner just won't show a slug if this fails.
    try {
      const { data } = await api.get("/store/brand/mine");
      setSlug(data?.slug || "");
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const loadSlots = useCallback(async (doctorId) => {
    const { data } = await api.get("/healthcare/doctor-slots", { params: { doctor_id: doctorId } });
    setSlots(data || []);
  }, []);

  // ---- Doctors ----
  const openProfile = (emp) => {
    setProfileFor(emp);
    setProfileForm({ specialization: emp.specialization || "", consult_fee: emp.consult_fee || 0 });
  };
  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await api.patch(`/employees/${profileFor.id}/doctor-profile`, {
        specialization: profileForm.specialization,
        consult_fee: Number(profileForm.consult_fee) || 0,
      });
      toast.success(`${profileFor.name} is now bookable as a doctor`);
      setProfileFor(null);
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save doctor profile");
    } finally {
      setSavingProfile(false);
    }
  };

  const openSlots = async (emp) => {
    setSlotsFor(emp);
    await loadSlots(emp.id);
  };
  const addSlot = async () => {
    setSavingSlot(true);
    try {
      await api.post("/healthcare/doctor-slots", { doctor_id: slotsFor.id, ...slotForm });
      await loadSlots(slotsFor.id);
      toast.success("Slot added");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add slot");
    } finally {
      setSavingSlot(false);
    }
  };
  const deleteSlot = async (slotId) => {
    try {
      await api.delete(`/healthcare/doctor-slots/${slotId}`);
      await loadSlots(slotsFor.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove slot");
    }
  };

  // ---- Appointments ----
  const setAppointmentStatus = async (appt, status) => {
    try {
      await api.patch(`/healthcare/appointments/${appt.id}/status`, { status });
      setAppointments((prev) => prev.map((a) => (a.id === appt.id ? { ...a, status } : a)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update appointment");
    }
  };

  const openNewAppt = () => {
    setNewAppt({
      doctor_id: doctors[0]?.id || "", date: todayStr(), slot_start: "",
      patient_name: "", patient_phone: "", consent: false, relation_note: "",
    });
    setApptExistingName(null);
    setNewApptSlots([]);
    setNewApptOpen(true);
  };

  const loadNewApptSlots = useCallback(async (doctorId, date) => {
    if (!doctorId || !date) return;
    setLoadingNewApptSlots(true);
    setNewAppt((f) => ({ ...f, slot_start: "" }));
    try {
      const { data } = await api.get("/care/doctor-availability", {
        params: { owner_id: user.id, doctor_id: doctorId, date },
      });
      setNewApptSlots(data || []);
    } catch (e) {
      setNewApptSlots([]);
      toast.error(e?.response?.data?.detail || "Could not load available times");
    } finally {
      setLoadingNewApptSlots(false);
    }
  }, [user.id]);

  useEffect(() => {
    if (newApptOpen && newAppt.doctor_id && newAppt.date) {
      loadNewApptSlots(newAppt.doctor_id, newAppt.date);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newApptOpen, newAppt.doctor_id, newAppt.date]);

  const submitNewAppt = async () => {
    if (!newAppt.slot_start) return toast.error("Pick a time slot");
    if (!newAppt.patient_name.trim() || !newAppt.patient_phone.trim()) {
      return toast.error("Enter the patient's name and phone number");
    }
    if (apptNameConflict && !newAppt.relation_note.trim()) {
      return toast.error(`Please note ${newAppt.patient_name.trim()}'s relationship to ${apptExistingName}`);
    }
    if (!newAppt.consent) return toast.error("Confirm the patient's consent to continue");
    setSavingAppt(true);
    try {
      await api.post("/healthcare/appointments", {
        doctor_id: newAppt.doctor_id,
        slot_start: newAppt.slot_start,
        patient_name: newAppt.patient_name,
        patient_phone: newAppt.patient_phone,
        consent_given: true,
        relation_note: apptNameConflict ? newAppt.relation_note.trim() : null,
      });
      toast.success("Appointment booked");
      setNewApptOpen(false);
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not book this appointment");
      loadNewApptSlots(newAppt.doctor_id, newAppt.date);
    } finally {
      setSavingAppt(false);
    }
  };

  // ---- Pathology catalog ----
  const openTest = (t) =>
    setTestDialog(t ? { ...t } : { name: "", price: 0, sample_type: "", report_tat_hours: 24 });
  const saveTest = async () => {
    if (!testDialog.name?.trim()) return toast.error("Test name is required");
    setSavingTest(true);
    try {
      const payload = {
        name: testDialog.name.trim(),
        price: Number(testDialog.price) || 0,
        sample_type: testDialog.sample_type || "",
        report_tat_hours: Number(testDialog.report_tat_hours) || 24,
      };
      if (testDialog.id) {
        await api.put(`/healthcare/pathology-catalog/${testDialog.id}`, payload);
      } else {
        await api.post("/healthcare/pathology-catalog", payload);
      }
      toast.success("Saved");
      setTestDialog(null);
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save test");
    } finally {
      setSavingTest(false);
    }
  };
  const deleteTest = async (id) => {
    try {
      await api.delete(`/healthcare/pathology-catalog/${id}`);
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete test");
    }
  };

  // ---- Pathology orders ----
  const setOrderStatus = async (order, status) => {
    try {
      await api.patch(`/healthcare/pathology-orders/${order.id}/status`, { status });
      setOrders((prev) => prev.map((o) => (o.id === order.id ? { ...o, status } : o)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update order");
    }
  };
  const uploadReport = async (order, file) => {
    if (!file) return;
    setUploadingReport(order.id);
    try {
      const { dataUrl } = await compressImage(file, { maxDimension: 2000 });
      await api.post(`/healthcare/pathology-orders/${order.id}/report`, { report_file: dataUrl });
      toast.success("Report uploaded — status set to delivered");
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not upload report");
    } finally {
      setUploadingReport(null);
    }
  };

  const openNewOrder = () => {
    setNewOrderTests(new Set());
    setNewOrder({
      collection_mode: "walk_in", address: "", collector_id: "",
      patient_name: "", patient_phone: "", consent: false, relation_note: "",
    });
    setOrderExistingName(null);
    setNewOrderOpen(true);
  };
  const toggleNewOrderTest = (id) =>
    setNewOrderTests((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const newOrderTotal = catalog
    .filter((t) => newOrderTests.has(t.id))
    .reduce((s, t) => s + Number(t.price || 0), 0);
  const submitNewOrder = async () => {
    if (newOrderTests.size === 0) return toast.error("Select at least one test");
    if (!newOrder.patient_name.trim() || !newOrder.patient_phone.trim()) {
      return toast.error("Enter the patient's name and phone number");
    }
    if (newOrder.collection_mode === "home" && !newOrder.address.trim()) {
      return toast.error("Enter a collection address");
    }
    if (orderNameConflict && !newOrder.relation_note.trim()) {
      return toast.error(`Please note ${newOrder.patient_name.trim()}'s relationship to ${orderExistingName}`);
    }
    if (!newOrder.consent) return toast.error("Confirm the patient's consent to continue");
    setSavingOrder(true);
    try {
      await api.post("/healthcare/pathology-orders", {
        test_ids: Array.from(newOrderTests),
        patient_name: newOrder.patient_name,
        patient_phone: newOrder.patient_phone,
        collection_mode: newOrder.collection_mode,
        address: newOrder.collection_mode === "home" ? newOrder.address : null,
        collector_id: newOrder.collector_id || null,
        consent_given: true,
        relation_note: orderNameConflict ? newOrder.relation_note.trim() : null,
      });
      toast.success("Pathology order booked");
      setNewOrderOpen(false);
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not book this order");
    } finally {
      setSavingOrder(false);
    }
  };

  if (enabled === null) return null;
  if (enabled === false) return <NotEnabled />;

  return (
    <div className="p-6 md:p-10 max-w-[1200px]" data-testid="owner-healthcare">
      <div className="mb-8">
        <div className="mono text-xs uppercase tracking-widest text-[#FF5000]">Field ops</div>
        <h1 className="font-heading text-3xl font-bold mt-2">Healthcare</h1>
        <p className="text-slate-500 mt-1 text-sm">
          Doctor slot booking and pathology services for your patients.
        </p>
        {slug ? (
          <div
            className="mt-3 inline-flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-md px-3 py-2"
            data-testid="healthcare-share-link"
          >
            <span className="mono text-xs text-slate-600">
              {window.location.origin}/care/{slug}
            </span>
            <button
              type="button"
              onClick={() => {
                navigator.clipboard.writeText(`${window.location.origin}/care/${slug}`);
                toast.success("Link copied");
              }}
              className="text-slate-400 hover:text-slate-700"
              title="Copy link"
              data-testid="healthcare-share-copy"
            >
              <Copy className="w-3.5 h-3.5" />
            </button>
            <a
              href={`/care/${slug}`}
              target="_blank"
              rel="noreferrer"
              className="text-slate-400 hover:text-slate-700"
              title="Open"
              data-testid="healthcare-share-open"
            >
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        ) : (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mt-3 inline-block">
            Set a public URL slug under Branding first — that's what patients use to reach your booking page.
          </p>
        )}
      </div>

      <div className="flex gap-1 p-1 bg-slate-100 rounded-lg mb-6 w-fit">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition ${
                tab === t.key ? "bg-white shadow-sm text-slate-900" : "text-slate-500"
              }`}
              data-testid={`healthcare-tab-${t.key}`}
            >
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "doctors" && (
        <Card className="border-slate-200 shadow-none overflow-hidden">
          <CardHeader className="border-b flex-row items-center justify-between space-y-0 py-3">
            <CardTitle className="font-heading text-sm">Employees</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 mono text-[10px] uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="text-left px-4 py-3">Name</th>
                  <th className="text-left px-4 py-3">Specialization</th>
                  <th className="text-left px-4 py-3">Consult fee</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {employees.map((e) => (
                  <tr key={e.id} data-testid={`healthcare-doctor-row-${e.id}`}>
                    <td className="px-4 py-3">{e.name}</td>
                    <td className="px-4 py-3 text-slate-600">{e.specialization || "—"}</td>
                    <td className="px-4 py-3 mono text-slate-600">
                      {e.consult_fee > 0 ? `₹${Number(e.consult_fee).toLocaleString("en-IN")}` : "—"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1.5">
                        <Button size="sm" variant="outline" onClick={() => openProfile(e)} data-testid={`healthcare-edit-doctor-${e.id}`}>
                          <Pencil className="w-3.5 h-3.5 mr-1" /> {e.specialization ? "Edit" : "Set as doctor"}
                        </Button>
                        {e.specialization && (
                          <Button size="sm" variant="outline" onClick={() => openSlots(e)} data-testid={`healthcare-slots-${e.id}`}>
                            <Clock className="w-3.5 h-3.5 mr-1" /> Slots
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {employees.length === 0 && (
                  <tr>
                    <td colSpan={4} className="text-center py-10 text-slate-500">
                      No employees yet — add one from the Employees page first.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {tab === "appointments" && (
        <Card className="border-slate-200 shadow-none overflow-hidden">
          <CardHeader className="border-b flex-row items-center justify-between space-y-0 py-3">
            <CardTitle className="font-heading text-sm">Appointments</CardTitle>
            <Button
              size="sm"
              className="bg-[#0055FF] hover:bg-[#0040CC]"
              onClick={openNewAppt}
              disabled={doctors.length === 0}
              title={doctors.length === 0 ? "Set up a doctor first" : undefined}
              data-testid="appointment-new"
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> New appointment
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 mono text-[10px] uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="text-left px-4 py-3">Patient</th>
                  <th className="text-left px-4 py-3">Doctor</th>
                  <th className="text-left px-4 py-3">Time</th>
                  <th className="text-left px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {appointments.map((a) => {
                  const doctor = employees.find((e) => e.id === a.doctor_id);
                  return (
                    <tr key={a.id} data-testid={`appointment-row-${a.id}`}>
                      <td className="px-4 py-3">
                        {a.patient_name}
                        {a.relation_note && <span className="text-slate-400"> ({a.relation_note})</span>}
                        <div className="mono text-[11px] text-slate-400">{a.patient_phone}</div>
                      </td>
                      <td className="px-4 py-3 text-slate-600">{doctor?.name || "—"}</td>
                      <td className="px-4 py-3 mono text-xs text-slate-600">{IST_STAMP(a.slot_start)}</td>
                      <td className="px-4 py-3">
                        <Select value={a.status} onValueChange={(v) => setAppointmentStatus(a, v)}>
                          <SelectTrigger className="w-36 h-8 text-xs" data-testid={`appointment-status-${a.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="booked">Booked</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                            <SelectItem value="no_show">No-show</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                    </tr>
                  );
                })}
                {appointments.length === 0 && (
                  <tr>
                    <td colSpan={4} className="text-center py-10 text-slate-500">
                      No appointments yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {tab === "catalog" && (
        <Card className="border-slate-200 shadow-none overflow-hidden">
          <CardHeader className="border-b flex-row items-center justify-between space-y-0 py-3">
            <CardTitle className="font-heading text-sm">Test catalog</CardTitle>
            <Button size="sm" className="bg-[#0055FF] hover:bg-[#0040CC]" onClick={() => openTest(null)} data-testid="catalog-add">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add test
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 mono text-[10px] uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="text-left px-4 py-3">Test</th>
                  <th className="text-left px-4 py-3">Sample</th>
                  <th className="text-left px-4 py-3">Price</th>
                  <th className="text-left px-4 py-3">TAT (hrs)</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {catalog.map((t) => (
                  <tr key={t.id} data-testid={`catalog-row-${t.id}`}>
                    <td className="px-4 py-3">{t.name}</td>
                    <td className="px-4 py-3 text-slate-600">{t.sample_type || "—"}</td>
                    <td className="px-4 py-3 mono text-slate-600">₹{Number(t.price).toLocaleString("en-IN")}</td>
                    <td className="px-4 py-3 mono text-slate-600">{t.report_tat_hours}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1.5">
                        <Button size="sm" variant="outline" onClick={() => openTest(t)}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => deleteTest(t.id)}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {catalog.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center py-10 text-slate-500">
                      No tests yet — add your first one.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {tab === "orders" && (
        <Card className="border-slate-200 shadow-none overflow-hidden">
          <CardHeader className="border-b flex-row items-center justify-between space-y-0 py-3">
            <CardTitle className="font-heading text-sm">Pathology orders</CardTitle>
            <Button
              size="sm"
              className="bg-[#0055FF] hover:bg-[#0040CC]"
              onClick={openNewOrder}
              disabled={catalog.length === 0}
              title={catalog.length === 0 ? "Add a test to the catalog first" : undefined}
              data-testid="order-new"
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> New order
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 mono text-[10px] uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="text-left px-4 py-3">Patient</th>
                  <th className="text-left px-4 py-3">Tests</th>
                  <th className="text-left px-4 py-3">Mode</th>
                  <th className="text-left px-4 py-3">Total</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-right px-4 py-3">Report</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {orders.map((o) => (
                  <tr key={o.id} data-testid={`order-row-${o.id}`}>
                    <td className="px-4 py-3">
                      {o.patient_name}
                      {o.relation_note && <span className="text-slate-400"> ({o.relation_note})</span>}
                      <div className="mono text-[11px] text-slate-400">{o.patient_phone}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-600 text-xs max-w-[220px]">
                      {(o.tests || []).map((t) => t.name).join(", ")}
                    </td>
                    <td className="px-4 py-3 text-slate-600 capitalize">{o.collection_mode?.replace("_", " ")}</td>
                    <td className="px-4 py-3 mono text-slate-600">₹{Number(o.total).toLocaleString("en-IN")}</td>
                    <td className="px-4 py-3">
                      <Select value={o.status} onValueChange={(v) => setOrderStatus(o, v)}>
                        <SelectTrigger className="w-32 h-8 text-xs" data-testid={`order-status-${o.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="booked">Booked</SelectItem>
                          <SelectItem value="collected">Collected</SelectItem>
                          <SelectItem value="processing">Processing</SelectItem>
                          <SelectItem value="delivered">Delivered</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <label className="inline-flex">
                        <span
                          className={`cursor-pointer inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border ${
                            uploadingReport === o.id ? "opacity-50 pointer-events-none" : "border-slate-200 hover:border-slate-300"
                          }`}
                        >
                          <Upload className="w-3 h-3" /> {uploadingReport === o.id ? "Uploading…" : "Upload"}
                        </span>
                        <input
                          type="file"
                          accept="image/*,application/pdf"
                          className="hidden"
                          onChange={(e) => uploadReport(o, e.target.files?.[0])}
                          data-testid={`order-upload-${o.id}`}
                        />
                      </label>
                    </td>
                  </tr>
                ))}
                {orders.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center py-10 text-slate-500">
                      No pathology orders yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* New appointment dialog — staff booking a walk-in / phone patient. */}
      <Dialog open={newApptOpen} onOpenChange={setNewApptOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>New appointment</DialogTitle>
            <DialogDescription>For a walk-in or a patient who called in — the patient booking themselves at your /care link works the same way.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-2">
              <Label>Doctor</Label>
              <Select
                value={newAppt.doctor_id}
                onValueChange={(v) => setNewAppt((f) => ({ ...f, doctor_id: v }))}
              >
                <SelectTrigger data-testid="new-appt-doctor"><SelectValue placeholder="Choose a doctor" /></SelectTrigger>
                <SelectContent>
                  {doctors.map((d) => (
                    <SelectItem key={d.id} value={d.id}>{d.name} · {d.specialization}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-appt-date">Date</Label>
              <Input
                id="new-appt-date"
                type="date"
                min={todayStr()}
                value={newAppt.date}
                onChange={(e) => setNewAppt((f) => ({ ...f, date: e.target.value }))}
                data-testid="new-appt-date"
              />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Available times</Label>
              {loadingNewApptSlots ? (
                <div className="text-sm text-slate-400 py-2">Checking availability…</div>
              ) : newApptSlots.length === 0 ? (
                <div className="text-sm text-slate-400 py-2">No open slots this day — try another date, or add slots from the Doctors tab.</div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {newApptSlots.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setNewAppt((f) => ({ ...f, slot_start: s }))}
                      className={`mono text-xs px-3 py-1.5 rounded-md border ${
                        newAppt.slot_start === s ? "bg-[#0055FF] text-white border-transparent" : "border-slate-200 hover:border-slate-300"
                      }`}
                      data-testid={`new-appt-slot-${s}`}
                    >
                      {IST_SLOT(s)}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="new-appt-name">Patient name</Label>
                <Input
                  id="new-appt-name"
                  value={newAppt.patient_name}
                  onChange={(e) => setNewAppt((f) => ({ ...f, patient_name: e.target.value }))}
                  data-testid="new-appt-patient-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-appt-phone">Phone</Label>
                <Input
                  id="new-appt-phone"
                  value={newAppt.patient_phone}
                  onChange={(e) => setNewAppt((f) => ({ ...f, patient_phone: e.target.value }))}
                  onBlur={checkApptPhone}
                  data-testid="new-appt-patient-phone"
                />
              </div>
            </div>
            {apptNameConflict && (
              <div className="space-y-2">
                <Label htmlFor="new-appt-relation">
                  This phone is on file for <b>{apptExistingName}</b> — relationship to {newAppt.patient_name.trim()}?
                </Label>
                <Input
                  id="new-appt-relation"
                  placeholder={`e.g. wife of ${apptExistingName}`}
                  value={newAppt.relation_note}
                  onChange={(e) => setNewAppt((f) => ({ ...f, relation_note: e.target.value }))}
                  data-testid="new-appt-relation"
                />
              </div>
            )}
            <label className="flex items-start gap-2.5 text-sm text-slate-600 cursor-pointer">
              <Checkbox
                checked={newAppt.consent}
                onCheckedChange={(v) => setNewAppt((f) => ({ ...f, consent: v }))}
                className="mt-0.5"
                data-testid="new-appt-consent"
              />
              <span>Patient has consented to their health information being collected for this booking.</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewApptOpen(false)} disabled={savingAppt}>
              Cancel
            </Button>
            <Button
              onClick={submitNewAppt}
              disabled={savingAppt || !newAppt.slot_start}
              className="bg-[#0055FF] hover:bg-[#0040CC]"
              data-testid="new-appt-submit"
            >
              {savingAppt ? "Booking…" : "Book appointment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New pathology order dialog — staff booking a walk-in / phone patient. */}
      <Dialog open={newOrderOpen} onOpenChange={setNewOrderOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>New pathology order</DialogTitle>
            <DialogDescription>For a walk-in or a patient who called in — booking at your /care link works the same way.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-2">
              <Label>Select tests</Label>
              <div className="border border-slate-200 rounded-lg divide-y max-h-48 overflow-y-auto">
                {catalog.map((t) => (
                  <label key={t.id} className="flex items-center justify-between gap-3 p-2.5 cursor-pointer">
                    <div className="flex items-center gap-2.5">
                      <Checkbox
                        checked={newOrderTests.has(t.id)}
                        onCheckedChange={() => toggleNewOrderTest(t.id)}
                        data-testid={`new-order-test-${t.id}`}
                      />
                      <span className="text-sm">{t.name}</span>
                    </div>
                    <span className="mono text-xs text-slate-500">₹{Number(t.price).toLocaleString("en-IN")}</span>
                  </label>
                ))}
              </div>
              {newOrderTests.size > 0 && (
                <div className="flex justify-between text-sm font-medium px-1">
                  <span>Total</span>
                  <span className="mono">₹{newOrderTotal.toLocaleString("en-IN")}</span>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Sample collection</Label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setNewOrder((f) => ({ ...f, collection_mode: "walk_in" }))}
                  className={`rounded-md border p-2 text-sm ${
                    newOrder.collection_mode === "walk_in" ? "border-[#0055FF] bg-blue-50" : "border-slate-200"
                  }`}
                  data-testid="new-order-walkin"
                >
                  Walk-in
                </button>
                <button
                  type="button"
                  onClick={() => setNewOrder((f) => ({ ...f, collection_mode: "home" }))}
                  className={`rounded-md border p-2 text-sm ${
                    newOrder.collection_mode === "home" ? "border-[#0055FF] bg-blue-50" : "border-slate-200"
                  }`}
                  data-testid="new-order-home"
                >
                  Home visit
                </button>
              </div>
            </div>

            {newOrder.collection_mode === "home" && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="new-order-address">Collection address</Label>
                  <Input
                    id="new-order-address"
                    value={newOrder.address}
                    onChange={(e) => setNewOrder((f) => ({ ...f, address: e.target.value }))}
                    data-testid="new-order-address"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Assign collector (optional)</Label>
                  <Select
                    value={newOrder.collector_id || "__unassigned"}
                    onValueChange={(v) => setNewOrder((f) => ({ ...f, collector_id: v === "__unassigned" ? "" : v }))}
                  >
                    <SelectTrigger data-testid="new-order-collector"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__unassigned">Unassigned — I'll assign it later</SelectItem>
                      {employees.map((e) => (
                        <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="new-order-name">Patient name</Label>
                <Input
                  id="new-order-name"
                  value={newOrder.patient_name}
                  onChange={(e) => setNewOrder((f) => ({ ...f, patient_name: e.target.value }))}
                  data-testid="new-order-patient-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-order-phone">Phone</Label>
                <Input
                  id="new-order-phone"
                  value={newOrder.patient_phone}
                  onChange={(e) => setNewOrder((f) => ({ ...f, patient_phone: e.target.value }))}
                  onBlur={checkOrderPhone}
                  data-testid="new-order-patient-phone"
                />
              </div>
            </div>
            {orderNameConflict && (
              <div className="space-y-2">
                <Label htmlFor="new-order-relation">
                  This phone is on file for <b>{orderExistingName}</b> — relationship to {newOrder.patient_name.trim()}?
                </Label>
                <Input
                  id="new-order-relation"
                  placeholder={`e.g. wife of ${orderExistingName}`}
                  value={newOrder.relation_note}
                  onChange={(e) => setNewOrder((f) => ({ ...f, relation_note: e.target.value }))}
                  data-testid="new-order-relation"
                />
              </div>
            )}
            <label className="flex items-start gap-2.5 text-sm text-slate-600 cursor-pointer">
              <Checkbox
                checked={newOrder.consent}
                onCheckedChange={(v) => setNewOrder((f) => ({ ...f, consent: v }))}
                className="mt-0.5"
                data-testid="new-order-consent"
              />
              <span>Patient has consented to their health information being collected for this booking.</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewOrderOpen(false)} disabled={savingOrder}>
              Cancel
            </Button>
            <Button
              onClick={submitNewOrder}
              disabled={savingOrder}
              className="bg-[#0055FF] hover:bg-[#0040CC]"
              data-testid="new-order-submit"
            >
              {savingOrder ? "Booking…" : "Book order"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Doctor profile dialog */}
      <Dialog open={!!profileFor} onOpenChange={(v) => !v && setProfileFor(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Doctor profile</DialogTitle>
            <DialogDescription>Setting a specialization is what makes {profileFor?.name} bookable by patients.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-2">
              <Label>Specialization</Label>
              <Input
                value={profileForm.specialization}
                onChange={(e) => setProfileForm((f) => ({ ...f, specialization: e.target.value }))}
                placeholder="e.g. General Physician"
                data-testid="doctor-specialization-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Consultation fee (₹)</Label>
              <Input
                type="number"
                value={profileForm.consult_fee}
                onChange={(e) => setProfileForm((f) => ({ ...f, consult_fee: e.target.value }))}
                data-testid="doctor-fee-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProfileFor(null)} disabled={savingProfile}>
              Cancel
            </Button>
            <Button onClick={saveProfile} disabled={savingProfile} className="bg-[#0055FF] hover:bg-[#0040CC]" data-testid="doctor-profile-save">
              {savingProfile ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Doctor slots dialog */}
      <Dialog open={!!slotsFor} onOpenChange={(v) => !v && setSlotsFor(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{slotsFor?.name}'s weekly schedule</DialogTitle>
            <DialogDescription>Patients can only book within these recurring windows.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-4 gap-2">
              <Select value={slotForm.day_of_week} onValueChange={(v) => setSlotForm((f) => ({ ...f, day_of_week: v }))}>
                <SelectTrigger className="col-span-1" data-testid="slot-day-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DAYS.map((d) => <SelectItem key={d.key} value={d.key}>{d.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input type="time" value={slotForm.start_time} onChange={(e) => setSlotForm((f) => ({ ...f, start_time: e.target.value }))} data-testid="slot-start-input" />
              <Input type="time" value={slotForm.end_time} onChange={(e) => setSlotForm((f) => ({ ...f, end_time: e.target.value }))} data-testid="slot-end-input" />
              <Input
                type="number"
                value={slotForm.slot_minutes}
                onChange={(e) => setSlotForm((f) => ({ ...f, slot_minutes: e.target.value }))}
                title="Minutes per patient"
                data-testid="slot-minutes-input"
              />
            </div>
            <Button size="sm" variant="outline" onClick={addSlot} disabled={savingSlot} data-testid="slot-add-submit">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add slot
            </Button>
            <div className="border-t border-slate-100 pt-3 space-y-1.5">
              {slots.map((s) => (
                <div key={s.id} className="flex items-center justify-between text-sm bg-slate-50 rounded px-3 py-2">
                  <span className="mono text-xs">
                    {DAYS.find((d) => d.key === s.day_of_week)?.label} · {s.start_time}–{s.end_time} · {s.slot_minutes}m
                  </span>
                  <button type="button" onClick={() => deleteSlot(s.id)} className="text-slate-400 hover:text-red-600">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              {slots.length === 0 && <p className="text-xs text-slate-400 text-center py-2">No slots yet.</p>}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add/edit test dialog */}
      <Dialog open={!!testDialog} onOpenChange={(v) => !v && setTestDialog(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{testDialog?.id ? "Edit test" : "Add test"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-2">
              <Label>Test name</Label>
              <Input value={testDialog?.name || ""} onChange={(e) => setTestDialog((f) => ({ ...f, name: e.target.value }))} data-testid="test-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Price (₹)</Label>
                <Input type="number" value={testDialog?.price ?? 0} onChange={(e) => setTestDialog((f) => ({ ...f, price: e.target.value }))} data-testid="test-price-input" />
              </div>
              <div className="space-y-2">
                <Label>Report TAT (hrs)</Label>
                <Input type="number" value={testDialog?.report_tat_hours ?? 24} onChange={(e) => setTestDialog((f) => ({ ...f, report_tat_hours: e.target.value }))} data-testid="test-tat-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Sample type</Label>
              <Input value={testDialog?.sample_type || ""} onChange={(e) => setTestDialog((f) => ({ ...f, sample_type: e.target.value }))} placeholder="e.g. Blood" data-testid="test-sample-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestDialog(null)} disabled={savingTest}>
              Cancel
            </Button>
            <Button onClick={saveTest} disabled={savingTest} className="bg-[#0055FF] hover:bg-[#0040CC]" data-testid="test-save">
              {savingTest ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
